import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AlertTriangle, ArrowLeft, X } from 'lucide-react'
import { API_BASE_URL, resolveFileUrl } from '../config/api'
import { pdfALienzoFirma } from '../utils/coordenadasFirma'
import { buildRechazoFormData } from '../utils/documentoEstadoUi'
import PDFViewer from '../components/PDFViewer'
import pdfjsLib from '../utils/pdfjsWrapper.js'

const VisorDocumento = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [titulo, setTitulo] = useState('')
  const [estado, setEstado] = useState('')
  const [pdfFile, setPdfFile] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [notas, setNotas] = useState([])
  const [notasLista, setNotasLista] = useState([])
  const [campos, setCampos] = useState([])
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(true)
  const [accion, setAccion] = useState(null)
  const [mostrarRechazo, setMostrarRechazo] = useState(false)
  const [rechazando, setRechazando] = useState(false)
  const [motivoRechazo, setMotivoRechazo] = useState('')

  const cerrarModalRechazo = () => {
    if (rechazando) return
    setMostrarRechazo(false)
    setMotivoRechazo('')
  }

  const formatearFecha = (valor) => {
    if (!valor) return ''
    const fecha = new Date(valor)
    if (Number.isNaN(fecha.getTime())) return ''
    return fecha.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })
  }

  useEffect(() => {
    let cancelado = false

    const abrirPdf = async (urls) => {
      let ultimoError = null
      for (const candidata of urls) {
        const pdfUrl = resolveFileUrl(candidata)
        if (!pdfUrl) continue
        try {
          const pdf = await pdfjsLib.getDocument(pdfUrl).promise
          return { pdf, pdfUrl }
        } catch (errorPdf) {
          ultimoError = errorPdf
        }
      }
      throw ultimoError || new Error('El PDF no está disponible')
    }

    const colocar = async (pdf, items, convertir) => {
      const visuales = []
      for (const item of items || []) {
        if (!item || item.pagina < 1 || item.pagina > pdf.numPages) continue
        if (item.en_archivo) continue
        const pdfPage = await pdf.getPage(item.pagina)
        const viewport = pdfPage.getViewport({ scale: 1.0 })
        visuales.push({
          ...convertir(item),
          ...pdfALienzoFirma({
            coordenadas_x: item.coordenadas_x,
            coordenadas_y: item.coordenadas_y,
            ancho: item.ancho,
            alto: item.alto,
            pdfWidth: viewport.width,
            pdfHeight: viewport.height,
          }),
        })
      }
      return visuales
    }

    const cargar = async () => {
      setCargando(true)
      setError('')
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/notas/documento/${id}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'No se pudo abrir el documento')
      }
      const data = await response.json()
      if (cancelado) return
      setTitulo(data.titulo || 'Documento')
      setEstado(data.estado || '')
      setAccion(data.accion_firmante || null)
      setNotasLista(data.notas || [])
      const urls = (data.pdf_urls && data.pdf_urls.length ? data.pdf_urls : [data.pdf_url]).filter(Boolean)
      if (!urls.length) throw new Error('El PDF no está disponible')
      const { pdf, pdfUrl } = await abrirPdf(urls)
      if (cancelado) return
      setPdfFile(pdfUrl)
      setTotalPages(pdf.numPages)
      setCurrentPage(1)
      setNotas(await colocar(pdf, data.notas, (nota) => ({
        id: nota.id,
        texto: nota.texto,
        autorNombre: nota.autor_nombre || 'Autor',
        fechaCreacion: nota.fecha_creacion,
        page: nota.pagina,
        guardada: true,
        propia: false,
      })))
      setCampos(await colocar(pdf, data.campos_firma, (campo) => ({
        id: campo.id,
        propio: false,
        bloqueado: true,
        page: campo.pagina,
        nombre: campo.nombre || 'Firmante',
        completado: !!campo.completado,
        estado_firmante: (campo.estado_firmante || '').toLowerCase(),
        color: (campo.estado_firmante || '').toLowerCase() === 'rechazado' ? '#b91c1c' : '#6b7280',
      })))
    }

    cargar()
      .catch((errorCarga) => {
        if (cancelado) return
        const mensaje = errorCarga.message || 'No se pudo abrir el documento'
        setError(mensaje)
        toast.error(mensaje)
      })
      .finally(() => {
        if (!cancelado) setCargando(false)
      })

    return () => {
      cancelado = true
    }
  }, [id])

  const tokenFirma = (accion?.ruta_firma || '').split('/editor/')[1] || ''

  const rechazarDocumento = async () => {
    if (!tokenFirma || rechazando) return
    setRechazando(true)
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/firmar/${tokenFirma}/rechazar`, {
        method: 'POST',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
        body: buildRechazoFormData(motivoRechazo),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Error al rechazar el documento')
      }
      toast.success('Documento rechazado exitosamente')
      setMostrarRechazo(false)
      setMotivoRechazo('')
      navigate('/documentos-pendientes')
    } catch (errorRechazo) {
      toast.error(errorRechazo.message || 'Error al rechazar el documento')
    } finally {
      setRechazando(false)
    }
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-50">
      <header className="shrink-0 border-b bg-white px-4 py-3 sm:px-6">
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1)
            } else {
              navigate('/documentos-pendientes')
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 hover:text-slate-950"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
          Volver
        </button>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold text-gray-900">{titulo}</h1>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">Solo lectura</span>
          {estado && <span className="text-xs text-gray-500">{estado}</span>}
        </div>
        <p className="text-xs text-gray-500">Puedes revisar el PDF, las páginas, las notas y los campos. Abrir esta vista no firma ni cambia el documento.</p>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-4 py-4 lg:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-white p-3 shadow-sm">
          {pdfFile ? (
            <PDFViewer
              file={pdfFile}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalPages={totalPages}
              signatures={[]}
              campos={campos}
              notas={notas}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-gray-600">
              {cargando ? 'Cargando documento...' : (error || 'No se pudo mostrar el PDF')}
            </div>
          )}
        </div>
        <aside className="w-full shrink-0 overflow-y-auto rounded-xl bg-white p-4 shadow-sm lg:w-80">
          <h2 className="font-semibold text-gray-900">Observaciones o Notas</h2>
          <p className="mt-1 text-xs text-gray-500">Solo lectura.</p>
          {notasLista.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">Este documento no tiene notas.</p>
          ) : (
            <ul className="mt-4">
              {notasLista.map((nota) => (
                <li key={nota.id} className="border-b border-gray-200 py-3 text-sm text-gray-900 last:border-b-0">
                  <p className="font-semibold">Nota de {nota.autor_nombre || 'Autor'}</p>
                  <p className="mt-1 whitespace-pre-wrap">{nota.texto}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Pág. {nota.pagina} · {formatearFecha(nota.fecha_creacion)}
                  </p>
                </li>
              ))}
            </ul>
          )}
          <h2 className="mt-6 font-semibold text-gray-900">Campos de firma</h2>
          {campos.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">No hay campos definidos.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              {campos.map((campo) => (
                <li key={campo.id}>
                  Pág. {campo.page} · {campo.nombre} ·{' '}
                  {campo.estado_firmante === 'rechazado'
                    ? 'Rechazado'
                    : (campo.completado ? 'Completado' : 'Pendiente')}
                </li>
              ))}
            </ul>
          )}
          {accion && (
            <div className="mt-6 border-t border-gray-200 pt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setMostrarRechazo(true)}
                className="rounded-md border border-red-600 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                Rechazar
              </button>
              <button
                type="button"
                disabled={!accion.puede_ir_a_firmar || !accion.ruta_firma}
                title={accion.en_espera ? 'Aún no es tu turno' : 'Ir a firmar'}
                onClick={() => { if (accion.puede_ir_a_firmar && accion.ruta_firma) navigate(accion.ruta_firma) }}
                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
              >
                Ir a firmar
              </button>
            </div>
          )}
        </aside>
      </div>
      {mostrarRechazo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={cerrarModalRechazo}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={cerrarModalRechazo}
              disabled={rechazando}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">¿Rechazar documento?</h3>
            <p className="text-gray-600 text-center mb-4">
              ¿Estás seguro de que deseas rechazar el documento{' '}
              <span className="font-semibold text-gray-900">"{titulo}"</span>?
            </p>
            <label className="block mb-6">
              <span className="block text-sm font-medium text-gray-700 mb-1.5">
                Motivo del rechazo (opcional)
              </span>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                disabled={rechazando}
                rows={3}
                maxLength={1000}
                placeholder="Describe el motivo si lo deseas"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </label>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={cerrarModalRechazo}
                disabled={rechazando}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={rechazarDocumento}
                disabled={rechazando}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium disabled:opacity-60"
              >
                {rechazando ? 'Rechazando...' : 'Sí, rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VisorDocumento
